import React, { useCallback, useMemo, useState } from 'react';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import moment from 'moment/moment';

import { DD_SLASH_MMM_SLASH_YYYY } from '../../../../../../../../constants/dateFormat';
import { muiDataGridLocaleText } from '../../../../../../../../services/i18n/mui';
import { ACTIONS_BUTTON, TABLE_CHECKBOX } from '../../../../../../finance/NewInvoice/InvoiceTable/TableConstants';
import { DefaultFinanceCell } from '../../../../../../finance/shared/FinanseTableMUI/DefaultFinanceCell';
import { convertPatientStatus } from '../../../../../../finance/SiteFinance/SiteFinanceTable/SiteFinanceTable';

export const ParticipantTable = ({
  participant,
  setSelectedParticipant,
  menuComponent,
  canUserPerformActions = true,
  'data-testid': dataTestId,
  ...restProps
}) => {
  const [pinnedColumns, setPinnedColumns] = useState({
    left: [TABLE_CHECKBOX],
    right: [ACTIONS_BUTTON]
  });

  const getTogglableColumns = useCallback(
    columns =>
      columns.filter(column => ![TABLE_CHECKBOX, ACTIONS_BUTTON].includes(column.field)).map(column => column.field),
    []
  );

  const columns = useMemo(() => {
    const columns = [
      {
        field: 'studyName',
        headerName: 'Study',
        minWidth: 150,
        flex: 1,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'pcn',
        headerName: 'PCN',
        minWidth: 150,
        flex: 1,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'siteName',
        headerName: 'Site',
        minWidth: 150,
        flex: 1,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'subjectId',
        headerName: 'Subject ID',
        minWidth: 100,
        flex: 1,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'patientId',
        headerName: 'Patient ID',
        minWidth: 150,
        flex: 1,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'patientStatus',
        headerName: 'Current Patient Status',
        minWidth: 150,
        flex: 1,
        valueGetter: value => convertPatientStatus(value),
        renderCell: DefaultFinanceCell
      },
      {
        field: 'icfConsentSignedDate',
        headerName: 'ICF Consent Signed',
        type: 'date',
        valueGetter: dateValueGetterWithoutLocalTimezone,
        renderCell: DateCell,
        minWidth: 150
      },
      {
        field: 'screenedDate',
        headerName: 'Screened Date',
        type: 'date',
        flex: 1,
        valueGetter: dateValueGetterWithoutLocalTimezone,
        renderCell: DateCell,
        minWidth: 150
      },
      {
        field: 'randomizedDate',
        headerName: 'Randomized Date',
        type: 'date',
        flex: 1,
        valueGetter: dateValueGetterWithoutLocalTimezone,
        renderCell: DateCell,
        minWidth: 150
      },
      {
        field: 'completedDate',
        headerName: 'Completed Date',
        type: 'date',
        flex: 1,
        valueGetter: dateValueGetterWithoutLocalTimezone,
        renderCell: DateCell,
        minWidth: 150
      },
      {
        field: ACTIONS_BUTTON,
        type: ACTIONS_BUTTON,
        resizable: false,
        width: 50,
        renderCell: ({ row }) => menuComponent(row)
      }
    ];
    return canUserPerformActions ? columns : columns.filter(column => column.field !== ACTIONS_BUTTON);
  }, [canUserPerformActions, menuComponent]);

  const handlePinnedColumnsChange = useCallback(updatedPinnedColumns => {
    setPinnedColumns({
      ...updatedPinnedColumns,
      left: [TABLE_CHECKBOX, ...updatedPinnedColumns.left.filter(column => column !== TABLE_CHECKBOX)],
      right: [...updatedPinnedColumns.right.filter(column => column !== ACTIONS_BUTTON), ACTIONS_BUTTON]
    });
  }, []);

  return (
    <DataGridPremium
      {...restProps}
      sx={{
        padding: '0 40px 0 40px',
        border: 'none',
        minHeight: '300px',
        '.MuiDataGrid-pinnedColumns, .MuiDataGrid-pinnedColumnHeaders': {
          backgroundColor: '#FAFAFA',
          paddingRight: '0 !important'
        },
        '.MuiTablePagination-root': {
          '& p': { margin: '1em 0 1em 0' }
        }
      }}
      getRowId={row => row?.ssuPatientId}
      rows={participant}
      columns={columns}
      rowHeight={38}
      isRowSelectable={params => params.row?.prime === 'false' || params.row?.prime === false}
      localeText={muiDataGridLocaleText({ noRowsLabel: 'No Record Found' })}
      disableRowGrouping
      disableAggregation
      disablePivoting
      checkboxSelection={canUserPerformActions}
      disableRowSelectionOnClick={!canUserPerformActions}
      onRowSelectionModelChange={newRowSelectionModel => {
        setSelectedParticipant(newRowSelectionModel);
      }}
      disableRowSelectionExcludeModel
      slotProps={{
        root: { 'data-testid': dataTestId },
        columnsManagement: {
          getTogglableColumns
        }
      }}
      pinnedColumns={pinnedColumns}
      onPinnedColumnsChange={handlePinnedColumnsChange}
      pagination
      pageSizeOptions={[5, 25, 50, 100]}
      initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
    />
  );
};

const DateCell = ({ value }) => <div>{value ? moment(value).format(DD_SLASH_MMM_SLASH_YYYY) : '—'}</div>;

export const dateValueGetterWithoutLocalTimezone = value => {
  if (value) {
    const date = new Date(value);
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() + userTimezoneOffset);
  }
  return null;
};
